import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  answerCurrent,
  battle,
  continueBattle,
  endBattle,
  fleeBattle,
  hintAvailable,
  timeoutCurrent,
  useAnyPotion,
  useHint,
  usePotion,
  type BattleState,
} from "@/battle/BattleController";
import { FLEE_COST } from "@/config";
import { getItem } from "@/content/items";
import { lt, t } from "@/i18n";
import { calculatorOpen, hero, itemCount, lang, settings } from "@/state/store";
import { ExplanationView, QuestionView } from "./QuestionView";

export function BattlePanel() {
  const b = battle.value;
  const now = useSignal(performance.now());

  useEffect(() => {
    const id = setInterval(() => {
      now.value = performance.now();
      const cur = battle.value;
      if (
        cur &&
        cur.phase === "question" &&
        cur.timerMs > 0 &&
        performance.now() - cur.questionStartedAt >= cur.timerMs
      ) {
        timeoutCurrent();
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cur = battle.value;
      if (!cur) return;
      const inInput = e.target instanceof HTMLInputElement;
      const key = e.key.toLowerCase();
      // letters never belong in a numeric answer, so they work as shortcuts even while typing
      if (cur.phase === "question" && (key === "h" || key === "p" || key === "k")) {
        e.preventDefault();
        if (key === "h") useHint();
        else if (key === "p") useAnyPotion();
        else if (settings.value.calculator) calculatorOpen.value = !calculatorOpen.value;
        return;
      }
      if (inInput) return;
      const confirm = e.key === "Enter" || e.key === " ";
      if (cur.phase === "feedback" && confirm) {
        e.preventDefault();
        continueBattle();
      } else if ((cur.phase === "won" || cur.phase === "lost" || cur.phase === "fled") && confirm) {
        e.preventDefault();
        endBattle();
      } else if (cur.phase === "question" && e.key === "Escape") {
        if (calculatorOpen.value) calculatorOpen.value = false;
        else fleeBattle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!b) return null;

  return (
    <div class="battle">
      <div class="battle-top">
        <img class="faceset small" src={b.monster.faceset} alt="" />
        <span class="mon-name">{lt(b.monster.name)}</span>
        <span class="mon-hp">
          {b.monsterHp}/{b.monster.hp} HP
        </span>
        {b.streak > 0 && <span class="streak">🔥 {t("battle.streak", { n: b.streak })}</span>}
      </div>
      {b.phase === "question" && b.question && <BattleQuestion b={b} now={now.value} />}
      {b.phase === "feedback" && b.feedback && <Feedback b={b} />}
      {(b.phase === "won" || b.phase === "lost" || b.phase === "fled") && <Result b={b} />}
    </div>
  );
}

function BattleQuestion({ b, now }: { b: BattleState; now: number }) {
  const h = hero.value;
  const L = lang.value;
  const canFlee = b.monster.rank !== "boss";
  const hint = hintAvailable();
  const hintLabel = hint.free
    ? t("battle.hintFree")
    : t("battle.hintScroll", { n: itemCount(h, "hint_scroll") });
  const potions = ["potion", "big_potion"].filter((id) => itemCount(h, id) > 0);
  return (
    <QuestionView
      question={b.question!}
      onAnswer={answerCurrent}
      timer={{ startedAt: b.questionStartedAt, ms: b.timerMs, now }}
      eliminated={b.eliminated}
      hint={b.hintText ? b.hintText[L] : undefined}
    >
      {canFlee && (
        <button class="flee" onClick={() => fleeBattle()} title="Esc">
          🏃 {t("battle.flee", { gold: Math.min(FLEE_COST, h.gold) })}
        </button>
      )}
      <button class="hint" disabled={!hint.free && !hint.scroll} onClick={() => useHint()} title="H">
        💡 {t("battle.hint")} {hintLabel}
      </button>
      {settings.value.calculator && (
        <button onClick={() => (calculatorOpen.value = !calculatorOpen.value)} title="K">
          🧮 {t("battle.calculator")}
        </button>
      )}
      {potions.map((id) => {
        const item = getItem(id);
        return (
          <button
            key={id}
            class="potion"
            disabled={b.heroHp >= b.heroHpMax}
            onClick={() => usePotion(id)}
            title="P"
          >
            {item.icon} {lt(item.name)} +{item.heal} ({itemCount(h, id)})
          </button>
        );
      })}
    </QuestionView>
  );
}

function Feedback({ b }: { b: BattleState }) {
  const f = b.feedback!;
  const L = lang.value;
  const monsterName = lt(b.monster.name);

  if (f.kind === "potion") {
    return (
      <div class="feedback potion">
        <div class="headline">{t("battle.potionUsed", { n: f.healed })}</div>
        <div class="detail">{t("battle.monsterHits", { m: monsterName, n: f.damageToHero })}</div>
        <button class="primary" onClick={() => continueBattle()}>
          {t("battle.continue")} ⏎
        </button>
      </div>
    );
  }

  return (
    <div class={`feedback ${f.correct ? "ok" : "bad"}`}>
      <div class="headline">
        {f.correct ? t("battle.correct") : f.timedOut ? t("battle.timeout") : t("battle.wrong")}
      </div>
      {f.correct ? (
        <div class="detail">
          {t("battle.youHit", { n: f.damageToMonster })}
          {f.quick && <span class="tag">{t("battle.quick")}</span>}
          {f.critical && <span class="tag crit">{t("battle.critical")}</span>}
        </div>
      ) : (
        <>
          <div class="detail">{t("battle.monsterHits", { m: monsterName, n: f.damageToHero })}</div>
          <ExplanationView correctAnswerText={f.correctAnswerText[L]} explanation={f.explanation[L]} />
        </>
      )}
      <button class="primary" onClick={() => continueBattle()}>
        {t("battle.continue")} ⏎
      </button>
    </div>
  );
}

function Result({ b }: { b: BattleState }) {
  const r = b.rewards!;
  const cls = b.phase === "won" ? "won" : b.phase === "fled" ? "fled" : "lost";
  return (
    <div class={`result ${cls}`}>
      <div class="headline">
        {b.phase === "won" ? t("battle.won") : b.phase === "fled" ? t("battle.fledTitle") : t("battle.lost")}
      </div>
      {b.phase === "won" && (
        <div class="detail">
          {t("battle.rewards", { xp: r.xp, gold: r.gold })}
          {r.leveledUp && <div class="levelup">{t("battle.levelUp", { lvl: r.newLevel })}</div>}
        </div>
      )}
      {b.phase === "lost" && <div class="detail">{t("battle.lostInfo", { gold: -r.gold })}</div>}
      {b.phase === "fled" && <div class="detail">{t("battle.fledInfo", { gold: -r.gold })}</div>}
      <button class="primary" onClick={() => endBattle()}>
        {t("battle.back")} ⏎
      </button>
    </div>
  );
}
